import axios from 'axios';
import { BASE_URL } from './urls';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Important for cookies
});

// Request interceptor to add auth header
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor for handling session invalidation
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check for session invalidation error
    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.code;

      // If session was invalidated (logged in elsewhere), force logout immediately
      if (errorCode === 'SESSION_INVALIDATED') {
        localStorage.removeItem('token');
        // Store message to show on login page
        sessionStorage.setItem('logoutMessage',
          'You have been logged out because your account was accessed from another device.');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Otherwise, try to refresh token (only once)
      if (!originalRequest._retry && !originalRequest.url?.includes('/auth/refresh-tokens')) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axiosInstance.post('/auth/refresh-tokens');
          const accessToken = response.data?.data?.accessToken;

          if (accessToken) {
            localStorage.setItem('token', accessToken);
            processQueue(null, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Check if refresh failed due to session invalidation
          if (refreshError.response?.data?.code === 'SESSION_INVALIDATED') {
            localStorage.removeItem('token');
            sessionStorage.setItem('logoutMessage',
              'You have been logged out because your account was accessed from another device.');
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
